import React, { createContext, useContext, useEffect, useState } from 'react';

// Support all themes - built-in and custom from themes.yaml
export type Theme = 'blue' | 'white' | 'black' | 'purple' | 'green' | 'orange' | 'financial' | 'healthcare' | 'empowerid' | string;

// localStorage key for user's theme preference
const THEME_STORAGE_KEY = 'experience-theme';
// localStorage key for admin-configured default (cached)
const ADMIN_DEFAULT_KEY = 'experience-admin-theme';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** The admin-configured default theme from branding.yaml */
  adminDefault: Theme;
  /** Whether user has explicitly chosen a theme (vs using admin default) */
  isUserChoice: boolean;
  /** Reset to admin default theme */
  resetToDefault: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'empowerid',
  setTheme: () => {},
  adminDefault: 'empowerid',
  isUserChoice: false,
  resetToDefault: () => {},
});

export const useTheme = () => useContext(ThemeContext);

/**
 * Get the initial theme on app load
 * Priority: User preference > Admin default > Fallback 'empowerid'
 */
function getInitialTheme(): { theme: Theme; isUserChoice: boolean } {
  const userTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
  const cachedAdminDefault = localStorage.getItem(ADMIN_DEFAULT_KEY) as Theme | null;
  
  if (userTheme) {
    // User has explicitly chosen a theme
    return { theme: userTheme, isUserChoice: true };
  }
  
  if (cachedAdminDefault) {
    // Use cached admin default (from previous branding fetch)
    return { theme: cachedAdminDefault, isUserChoice: false };
  }
  
  // Fallback until branding API loads (matches branding.yaml theme.mode)
  return { theme: 'empowerid', isUserChoice: false };
}

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const initial = getInitialTheme();
  const [theme, setThemeState] = useState<Theme>(initial.theme);
  const [adminDefault, setAdminDefault] = useState<Theme>('empowerid');
  const [isUserChoice, setIsUserChoice] = useState(initial.isUserChoice);

  // Fetch admin-configured default theme from branding API
  useEffect(() => {
    fetch('/api/configs/branding', { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(branding => {
        if (branding?.theme?.mode) {
          const configuredDefault = branding.theme.mode as Theme;
          console.log(`[ThemeContext] Admin default theme: ${configuredDefault}`);
          
          const previousAdminDefault = localStorage.getItem(ADMIN_DEFAULT_KEY);
          localStorage.setItem(ADMIN_DEFAULT_KEY, configuredDefault);
          setAdminDefault(configuredDefault);
          
          const userTheme = localStorage.getItem(THEME_STORAGE_KEY);
          
          // If admin default changed and user's stored theme matches the OLD default,
          // they were just using the default (not an explicit choice) — follow the new default
          if (previousAdminDefault && previousAdminDefault !== configuredDefault && userTheme === previousAdminDefault) {
            console.log(`[ThemeContext] Admin default changed ${previousAdminDefault} → ${configuredDefault}, updating user`);
            localStorage.removeItem(THEME_STORAGE_KEY);
            setThemeState(configuredDefault);
            setIsUserChoice(false);
            return;
          }
          
          // If user hasn't chosen, apply admin default
          if (!userTheme && theme !== configuredDefault) {
            console.log(`[ThemeContext] Applying admin default: ${configuredDefault}`);
            setThemeState(configuredDefault);
            setIsUserChoice(false);
          }
        }
      })
      .catch(err => console.warn('[ThemeContext] Failed to fetch admin default:', err));
  }, []);

  // Custom setTheme that tracks user choice
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    setIsUserChoice(true);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  };

  // Reset to admin default
  const resetToDefault = () => {
    localStorage.removeItem(THEME_STORAGE_KEY);
    setThemeState(adminDefault);
    setIsUserChoice(false);
    console.log(`[ThemeContext] Reset to admin default: ${adminDefault}`);
  };

  useEffect(() => {
    // Apply theme class to document root
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.classList.add(`theme-${theme}`);
    
    // Only persist theme when user explicitly chose it
    if (isUserChoice) {
      localStorage.setItem('experience-theme', theme);
    }
    
    // Apply theme-specific body styles
    document.body.setAttribute('style', `
      background: var(--main-bg) !important;
      color: var(--main-text) !important;
    `);
    
    // For themes not in CSS, fetch colors AND branding from themes API
    const themesWithCSS = ['blue', 'white', 'black'];
    if (!themesWithCSS.includes(theme)) {
      console.log(`[ThemeContext] Fetching theme data for: ${theme}`);
      
      fetch('/api/configs/branding/themes', { credentials: 'include' })
        .then(res => res.ok ? res.json() : {})
        .then(async (themes: Record<string, any>) => {
          const themeData = themes[theme];
          if (!themeData) {
            console.warn(`[ThemeContext] Theme not found: ${theme}`);
            return;
          }
          
          // Apply colors via ThemeManager (unified system)
          if (themeData.colors) {
            const { themeManager } = await import('../core/theme/ThemeManager');
            themeManager.setColors('theme', themeData.colors);
            console.log(`[ThemeContext] ✅ Applied ${Object.keys(themeData.colors).length} colors for theme: ${theme}`);
          }
          
          // Apply branding if theme has it
          if (themeData.branding) {
            console.log(`[ThemeContext] ✅ Applying branding from theme: ${themeData.branding.appName}`);
            
            // Store theme branding
            (window as any).__THEME_BRANDING__ = themeData.branding;
            
            // Update document title
            if (themeData.branding.appName) {
              document.title = themeData.branding.appName;
            }
            
            // Dispatch event for components
            window.dispatchEvent(new CustomEvent('theme-branding-changed', { 
              detail: themeData.branding 
            }));
          } else {
            // Theme has no branding - clear any previous theme branding
            if ((window as any).__THEME_BRANDING__) {
              console.log(`[ThemeContext] ✅ Clearing theme branding`);
              delete (window as any).__THEME_BRANDING__;
              
              // Revert to default branding
              const defaultBranding = (window as any).__EXPERIENCE_BRANDING__;
              if (defaultBranding?.appName) {
                document.title = defaultBranding.appName;
              }
              
              // Dispatch clear event
              window.dispatchEvent(new CustomEvent('theme-branding-changed', { 
                detail: null 
              }));
            }
          }
        })
        .catch(err => console.error('[ThemeContext] Failed to load theme:', err));
    } else {
      // CSS-based theme - clear ALL ThemeManager colors to let base CSS handle it
      // This includes both 'theme' AND 'branding' colors because:
      // 1. If BFF auto-loaded theme colors into branding response (e.g., admin set mode: "daimler")
      // 2. Those colors would override CSS themes since inline styles beat base CSS
      // 3. Clearing 'branding' ensures CSS themes work correctly
      import('../core/theme/ThemeManager').then(({ themeManager }) => {
        themeManager.clearColors('theme');
        // Also clear branding colors when user explicitly chooses a CSS theme
        // This ensures admin-configured theme colors don't override user's CSS theme choice
        if (isUserChoice) {
          themeManager.clearColors('branding');
          console.log(`[ThemeContext] ✅ Cleared branding colors for CSS theme: ${theme}`);
        }
      });
      
      // CSS-based theme - clear theme branding
      if ((window as any).__THEME_BRANDING__) {
        console.log(`[ThemeContext] ✅ Clearing theme branding (CSS theme: ${theme})`);
        delete (window as any).__THEME_BRANDING__;
        
        const defaultBranding = (window as any).__EXPERIENCE_BRANDING__;
        if (defaultBranding?.appName) {
          document.title = defaultBranding.appName;
        }
        
        window.dispatchEvent(new CustomEvent('theme-branding-changed', { 
          detail: null 
        }));
      }
    }
    
    // Use MutationObserver to ensure data-theme stays set
    const observer = new MutationObserver(() => {
      if (document.documentElement.getAttribute('data-theme') !== theme) {
        document.documentElement.setAttribute('data-theme', theme);
      }
    });
    
    observer.observe(document.documentElement, { 
      attributes: true,
      attributeFilter: ['data-theme']
    });
    
    return () => observer.disconnect();
  }, [theme, isUserChoice]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, adminDefault, isUserChoice, resetToDefault }}>
      {children}
    </ThemeContext.Provider>
  );
};